<template>
  <div
    v-bind:class="{
      header: true,
      'header-demo': isPresentation,
    }"
  >
    <div class="header-content">
      <EnvLabel
        :env="isPresentation ? 'demo' : 'dev'"
        v-bind:onClick="onEnvLabelClick"
      />
      <PopupBackdrop
        v-bind:isDisabled="true"
        v-if="showDemoPopup && isPresentation"
      >
        <div class="popup popup-demo-accept">
          <DemoAcceptPopup v-bind:onButtonClick="acceptDemo" />
        </div>
      </PopupBackdrop>

      <PopupBackdrop v-if="popup.activePopupId === 'ask' && isPresentation">
        <div class="popup popup-ask">
          <AskBlock v-bind:onCloseClick="closePopup" />
        </div>
      </PopupBackdrop>

      <PopupBackdrop v-if="popup.activePopupId === 'demo' && isPresentation">
        <div class="popup popup-demo">
          <DemoBlock
            v-bind:onCloseClick="closePopup"
            v-bind:showContact="isPresentation"
            v-bind:onButtonClick="
              () => {
                closePopup();
                togglePopup('ask');
              }
            "
          />
        </div>
      </PopupBackdrop>

      <PopupBackdrop v-if="popup.activePopupId === 'lang'">
        <div class="popup popup-lang">
          <LanguageSelect
            :onCloseClick="closePopup"
            v-bind:toggleButtonRef="$refs.languageToggleButtonRef"
          />
        </div>
      </PopupBackdrop>

      <PopupBackdrop v-if="popup.activePopupId === 'theme'">
        <div class="popup popup-theme">
          <ThemesBlock v-bind:onCloseClick="closePopup" />
        </div>
      </PopupBackdrop>
      <PopupBackdrop
        v-if="popup.activePopupId === 'dev-config' && !isPresentation"
      >
        <div class="popup popup-dev-config">
          <DevConfigBlock v-bind:onCloseClick="closePopup" />
        </div>
      </PopupBackdrop>

      <div class="header-left">
        <template>
          <a
            :href="
              isBetbyGames
                ? 'https://demo.betby.games'
                : 'https://demo.betby.com'
            "
          >
            <img
              class="logo-icon"
              src="/static/betby-logo.svg"
              alt="Betby"
              border="0"
              v-if="!isBetbyGames"
            />
            <img
              class="logo-icon betby-games-logo-icon"
              src="/static/betby-games-logo.svg"
              alt="Betby"
              border="0"
              v-if="isBetbyGames"
            />

            <img
              class="logo-icon logo-icon--mobile"
              src="/static/betby-logo-mobile.svg"
              alt="Betby"
              border="0"
              v-if="!isBetbyGames"
            />
            <img
              class="logo-icon logo-icon--mobile"
              src="/static/betby-games-logo-mobile.svg"
              alt="Betby"
              border="0"
              v-if="isBetbyGames"
            />
          </a>
          <HeaderIcon
            src="/static/icon-ask.svg"
            class="ask-icon"
            v-if="isPresentation"
            v-bind:onClick="() => togglePopup('ask')"
          />
        </template>
      </div>

      <div
        class="header-center"
        v-shortkey="['ctrl', 'h']"
        @shortkey="toggleEditor()"
      >
        <HeaderIcon
          :iconSize="16"
          class="builder"
          src="/static/builder.svg"
          v-bind:onClick="() => toggleBuilder()"
          v-if="showEditor"
        />
        <HeaderIcon
          :iconSize="16"
          class="editor"
          src="/static/palette.svg"
          v-bind:onClick="() => toggleEditor()"
          v-if="showEditor"
          v-bind:showEditorPin="showEditorPin"
        />
        <HeaderIcon
          :iconSize="16"
          src="/static/layout-icon.svg"
          class="color-settings-icon-mobile"
          v-bind:onClick="() => togglePopup('theme')"
        />
        <HeaderIcon
          :iconSize="16"
          label="LAYOUT"
          src="/static/layout-icon.svg"
          class="color-settings-icon"
          v-bind:onClick="() => togglePopup('theme')"
        />
      </div>

      <div class="header-right">
        <HeaderIcon
          src="/static/icon-color-settings.svg"
          :iconSize="19"
          v-bind:onClick="() => togglePopup('dev-config')"
          v-if="!isPresentation"
        />

        <div
          @click="() => togglePopup('lang')"
          class="header-lang"
          ref="languageToggleButtonRef"
        >
          <img
            :src="getLanguageFlagName(lang)"
            :alt="getLanguageName(lang)"
            height="24"
            width="24"
            class="header-lang__img"
          />
          <span>{{ lang }}</span>
        </div>

        <Balance
          v-if="isLoggedIn"
          :onClick="togglePopup"
          class="header-balance"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import createWS from "../../api/ws";
import EnvLabel from "./EnvLabel";
import HeaderIcon from "./HeaderIcon";
import SettingsIcon from "../../../static/settings.svg";
import AskBlock from "./AskBlock";
import ThemesBlock from "./ThemesBlock";
import DemoBlock from "./DemoBlock";
import DevConfigBlock from "./DevConfigBlock";
import Balance from "../Balance";
import LanguageSelect from "../LanguageSelect";
import DemoAcceptPopup from "./DemoAcceptPopup";
import storage from "../../store/storage";
import PopupBackdrop from "./PopupBackdrop";
import {
  getLanguageFlagName,
  getLanguageName,
} from "../../helpers/getLanguageFlagName";

export default {
  name: "Header",
  components: {
    PopupBackdrop,
    Balance,
    SettingsIcon,
    HeaderIcon,
    AskBlock,
    ThemesBlock,
    DevConfigBlock,
    DemoBlock,
    EnvLabel,
    LanguageSelect,
    DemoAcceptPopup,
  },
  data() {
    return {
      showEditorPin: false,
    };
  },
  computed: {
    showDemoPopup() {
      return this.visible && this.$store.state.app.isPresentation;
    },
    showEditor() {
      return (
        process.env.ENV === "dev" || process.env.NODE_ENV === "development"
      );
    },
    ...mapState("app", [
      "isPresentation",
      "popup",
      "showDemoPopup",
      "isBetbyGames",
      "activeEnv",
    ]),
    ...mapState("auth", ["isLoggedIn", "lang"]),
  },
  created() {
    this.getLanguageFlagName = getLanguageFlagName;
    this.getLanguageName = getLanguageName;

    window.addEventListener("keydown", this.handleShortCut);
    const token = storage.getItem("token");

    this.checkEditorPin();
    window.addEventListener("bt-editor-theme-storage", this.onStorageChange);

    if (this.isPresentation) {
      if (!token) {
        this.makeTempUser();
      } else {
        this.$store.dispatch("auth/getUser", token);
      }
    } else {
      this.$store.dispatch("auth/getUser", token);
    }

    this.$store.dispatch("app/getThemes");
  },
  destroyed() {
    window.removeEventListener("keydown", this.handleShortCut);
    window.addEventListener("bt-editor-theme-storage", this.onStorageChange);
  },
  methods: {
    checkEditorPin(event) {
      const editorData = localStorage.getItem("bt-editor-theme");
      this.showEditorPin = !!editorData;
    },
    onStorageChange() {
      this.checkEditorPin();
    },
    handleShortCut(e) {
      if (e.keyCode === 84 && e.shiftKey) {
        // ctrl+e
        this.togglePopup("theme");
      }
    },
    toggleEditor() {
      this.$store.dispatch("app/toggleEditor");
    },
    toggleBuilder() {
      this.$store.dispatch("app/toggleBuilder");
    },
    onEnvLabelClick() {
      if (this.isPresentation) {
        this.togglePopup("demo");
      }
    },
    makeTempUser() {
      const randUserId = 100000 + Math.floor(Math.random() * 900000);
      const userName = `User-${randUserId}-${Date.now()}`;
      const email = `${userName}@domain.com`;

      return this.$store.dispatch("auth/register", {
        email,
        password: "123",
        username: userName,
        lang: "en-GB",
        oddsFormat: "DECIMAL",
        expireIn: 604800,
      });
    },
    togglePopup(id) {
      this.$store.dispatch("app/togglePopup", id);
    },
    closePopup() {
      this.$store.dispatch("app/togglePopup", null);
    },
    connectToWS() {
      this.ws = createWS(this.activeEnv);
      this.ws.onEvent("balance", (balance) => {
        return this.$store.commit("balance/BALANCE_SUCCESS", [balance]);
      });
    },
    acceptDemo() {
      this.$store.dispatch("app/closeDemoPopup");
    },
  },
  watch: {
    isLoggedIn(isLoggedIn) {
      if (isLoggedIn) {
        return this.connectToWS();
      }

      this.ws.close();
    },
  },
};
</script>

<style scoped>
.settings-icon img {
  width: 21px;
  height: 21px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  cursor: pointer;
  transition: opacity 0.5s;
}
.settings-icon img:hover {
  opacity: 0.5;
}

.popup {
  position: absolute;
  top: 16px;
}
.popup-ask,
.popup-demo {
  left: 16px;
}
.popup-demo-accept {
  left: calc(50% - 400px);
}
.popup-theme {
  left: 50%;
  transform: translate(-50%, 0);
}

.popup-dev-config,
.popup-lang {
  right: 16px;
}

.header {
  width: 100%;
  display: flex;
  flex-direction: column;
  color: #3b4c58;
}

.header-content {
  position: relative;
  box-sizing: border-box;
  height: 64px;
  padding: 0 20px 0 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
  white-space: nowrap;
  background-color: #172329;
  font-size: 0;
}

.header-left {
  flex: 1;
  display: flex;
  align-items: center;
}
.header-center {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
.header-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.builder {
  margin-right: 4px;
  border-radius: 16px 4px 4px 16px;
}
.editor {
  margin-right: 16px;
  border-radius: 4px 16px 16px 4px;
}
.color-settings-icon-mobile {
  display: none;
}
.header-balance {
  margin-left: 8px;
}
.header-lang {
  margin-left: 16px;
  display: flex;
  align-items: center;
  height: 32px;
  background: #2a3f4c;
  border-radius: 16px;
  padding: 0 10px 0 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  text-transform: uppercase;
}
.header-lang__img {
  margin-right: 8px;
  border-radius: 50%;
  overflow: hidden;
}
.header-lang__img img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.logo-icon {
  width: 182px;
  height: auto;
  margin-right: 16px;
}
.logo-icon.betby-games-logo-icon {
  width: 351px;
  height: auto;
  margin-right: 16px;
}
.logo-icon--mobile {
  width: 48px;
  display: none;
}
.logo-icon--mobile.betby-games-logo-icon {
  width: 48px;
  display: none;
}
.ask-icon {
  width: 32px;
  height: 32px;
  margin-right: 16px;
}

@media (max-width: 374px) {
  .header-demo .color-settings-icon {
    display: none;
  }
  .header-demo .color-settings-icon-mobile {
    margin-left: 0;
    display: flex;
  }
}

@media (max-width: 700px) {
  .color-settings-icon {
    display: none;
  }
  .color-settings-icon-mobile {
    margin-left: 0;
    display: flex;
  }
}

@media (max-width: 600px) {
  .logo-icon {
    display: none;
  }
  .logo-icon--mobile {
    display: block;
    margin-right: 16px;
  }
  .header-content {
    height: 56px;
    padding-right: 12px;
    justify-content: space-between;
    padding-left: 36px;
  }
  .header-left {
    flex: 0 0 auto;
  }
  .header-center {
    flex: 0 0 auto;
    justify-content: flex-start;
  }
  .header-right {
    flex: 1;
  }

  .builder {
    display: none !important;
  }
  .editor {
    display: none !important;
  }
  .popup-demo {
    left: 50%;
    right: initial;
    transform: translateX(-50%);
  }

  .popup-ask,
  .popup-theme,
  .popup-dev-config,
  .popup-lang {
    left: 8px;
    right: 0;
    width: calc(100% - 16px);
    transform: translate(0, 0);
  }
}

@media (max-width: 800px) {
  .popup-demo-accept {
    left: 0;
    margin: 0 8px;
  }
}
</style>
